import { Button, Col, Input, Row, Spin } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useContext, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AppContext } from 'context/AppContext';
import { BioPolishService } from 'services/BioPolishService';

const { TextArea } = Input;

interface EditBioProps {
  closeHandler?: () => void;
  isPolishing?: boolean;
  actionType?: 'polish' | 'generation';
  onPolishComplete?: (polishedBio: string) => void;
}

const EditBio: React.FC<EditBioProps> = ({ closeHandler, isPolishing, actionType = 'polish', onPolishComplete }) => {
  const { t } = useTranslation();
  const { api, commonData, refetchUserdetails, authentication } = useContext(AppContext);
  const { userDetails } = commonData;
  const { authKey } = authentication;
  const [isLoading, setIsLoading] = useState(false);
  const [value, setValue] = useState(userDetails?.profile?.bio ?? '');
  const [isPolishingBio, setIsPolishingBio] = useState(false);
  const polishRequestRef = useRef(false);

  useEffect(() => {
    if (!isPolishing || !userDetails?.id || !authKey || isPolishingBio || polishRequestRef.current) return;
    
    // Use ref to prevent duplicate calls even in StrictMode
    polishRequestRef.current = true;
    setIsPolishingBio(true);
    
    BioPolishService.getUserBio(
      userDetails.id,
      actionType,
      authKey
    ).then((polishedBio) => {
      setValue(polishedBio);
      onPolishComplete?.(polishedBio);
    }).catch((error) => {
      console.error('Bio polish error:', error);
    }).finally(() => {
      setIsPolishingBio(false);
    });
  }, [isPolishing, userDetails?.id, authKey, isPolishingBio, actionType, onPolishComplete]);

  // Reset ref when modal closes
  useEffect(() => {
    if (!isPolishing) {
      polishRequestRef.current = false;
    }
  }, [isPolishing]);


  const updateBio = async () => {
    setIsLoading(true);
    const userId = userDetails?.id ?? '';
    await api.profile.updateUserDetails(userId, { profile: { bio: value } });
    await refetchUserdetails.refetch();
    setIsLoading(false);
    closeHandler?.();
  };

  return (
    <>
      <Row justify={'space-between'} style={{ margin: '24px 0' }}>
        <Col>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={closeHandler} />
        </Col>
        <Col style={{ fontSize: 18 }}>{t('#Profile.update_bio')}</Col>
        <Col>
          <Button
            type="text"
            loading={isLoading}
            onClick={updateBio}>{t('#Profile.done')}</Button>
        </Col>
      </Row>
      {isPolishingBio && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, fontSize: 16 }}>
            {actionType === 'generation' ? t('#Profile.generating_bio') : t('#Profile.polishing_bio')}
          </div>
        </div>
      )}
      
      {!isPolishingBio && (
        <TextArea
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder={t('#Profile.add_bio')}
          autoSize={{ minRows: 10, maxRows: 30 }}
        />
      )}
    </>
  );
};

export default EditBio;
